import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import ChatWidget from "../components/ChatWidget";
import api from "../lib/api";
import { MapPin, Phone, Clock, ArrowRight, Instagram, QrCode, Flame, PiggyBank, Banknote, ShieldCheck, Zap } from "lucide-react";

const IMG = {
  burger: "https://images.unsplash.com/photo-1713330801172-03f8d1c0dde7?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  tost: "https://images.pexels.com/photos/15110224/pexels-photo-15110224.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  kumru: "https://images.unsplash.com/photo-1550507992-eb63ffee0847?crop=entropy&cs=srgb&fm=jpg&q=85&w=940",
  fries: "https://images.unsplash.com/photo-1630384060421-cb20d0e0649d?crop=entropy&cs=srgb&fm=jpg&q=85&w=940",
  toast2: "https://images.pexels.com/photos/1195941/pexels-photo-1195941.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  cheese: "https://images.unsplash.com/photo-1639744093483-86714cd62a3b?crop=entropy&cs=srgb&fm=jpg&q=85&w=940",
};

const lezzetler = [
  { name: "Makedon Burger", desc: "Köfte, cheddar, mor soğan, özel sos", img: IMG.burger, hot: true },
  { name: "Sanayi Tostu", desc: "Kaşarlı, sucuklu, karışık — bol malzeme", img: IMG.tost },
  { name: "Kumru", desc: "Sosis, kaşar, turşu, domates", img: IMG.kumru },
  { name: "Çıtır Patates", desc: "Sıcacık, çıtır çıtır", img: IMG.fries },
  { name: "Yumurtalı Tost", desc: "Yumurta, sucuk, kaşar, marul", img: IMG.toast2 },
  { name: "Peynirli Atıştırmalıklar", desc: "Mozzarella sticks & daha fazlası", img: IMG.cheese },
];

export default function HomePage() {
  const [s, setS] = useState({});
  useEffect(() => {
    api.get("/settings").then((r) => setS(r.data || {})).catch(() => {});
    api.get("/warmup").catch(() => {});
  }, []);

  return (
    <div className="relative overflow-hidden bg-[#0B0B0C] min-h-screen">
      <Navbar />

      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center pt-24 pb-14">
        <div className="absolute inset-0">
          <img src={IMG.burger} alt="CAFE-İN lezzetleri" className="w-full h-full object-cover opacity-35" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0B0B0C]/70 via-[#0B0B0C]/85 to-[#0B0B0C]" />
          <div className="absolute inset-0 grain" />
        </div>

        <div className="relative max-w-5xl mx-auto px-5 sm:px-8 text-center w-full fade-up">
          <img src="/cafein-wordmark.png" alt="CAFE-İN" className="h-28 sm:h-36 mx-auto drop-shadow-[0_4px_20px_rgba(212,175,55,0.2)]" />
          <p className="mt-2 font-mono text-[11px] sm:text-xs uppercase tracking-[0.3em] text-[#E5BF47]">Osmangazi • Bursa</p>
          <h1 className="mt-5 font-serif text-3xl sm:text-5xl font-semibold text-white leading-tight">
            Tost, Burger, Kumru & Kahve
          </h1>
          <p className="mt-4 text-zinc-300 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
            {s.about || "Mahallenin sıcak buluşma noktası. Taze tostlar, enfes burgerler ve çıtır atıştırmalıklar — masandan ya da paket, birkaç dokunuşla sipariş ver."}
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/menu" className="btn-gold px-8 py-4 rounded-xl text-base flex items-center justify-center gap-2" data-testid="hero-order-btn">
              Sipariş Ver <ArrowRight size={18} />
            </Link>
            <a href="#lezzetler" className="btn-outline px-8 py-4 rounded-xl text-base flex items-center justify-center gap-2" data-testid="hero-menu-btn">
              <QrCode size={18} /> Menüye Bak
            </a>
          </div>
          <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 justify-center text-sm text-zinc-400">
            <span className="flex items-center gap-2"><Clock size={15} className="text-[#D4AF37]" /> {s.hours || "09:00 - 23:00"}</span>
            <span className="flex items-center gap-2"><MapPin size={15} className="text-[#D4AF37]" /> Osmangazi / Bursa</span>
          </div>
        </div>
      </section>

      {/* LEZZETLER */}
      <section id="lezzetler" className="relative py-20 px-5 sm:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <span className="font-mono text-xs uppercase tracking-[0.3em] text-[#E5BF47]">Menüden Seçmeler</span>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl font-semibold text-white">Canın Ne Çekiyorsa</h2>
            <p className="mt-3 text-zinc-400 text-sm max-w-lg mx-auto">Sıcacık, taptaze ve doyurucu. İşte müşterilerimizin favorileri.</p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-5">
            {lezzetler.map((h, i) => (
              <Link to="/menu" key={h.name} className="group card-lux card-hover overflow-hidden fade-up block" style={{ animationDelay: `${i * 0.06}s` }} data-testid={`lezzet-${i}`}>
                <div className="relative aspect-square sm:aspect-[4/3] overflow-hidden">
                  <img src={h.img} alt={h.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0C] via-transparent to-transparent" />
                  {h.hot && <span className="absolute top-3 left-3 flex items-center gap-1 text-[10px] font-mono px-2 py-1 rounded-full bg-[#0B0B0C]/80 text-[#FFE599] border border-[#D4AF37]/30"><Flame size={10} />Popüler</span>}
                  <div className="absolute bottom-0 inset-x-0 p-3 sm:p-4">
                    <h3 className="font-serif text-base sm:text-xl text-white leading-tight">{h.name}</h3>
                    <p className="text-[11px] sm:text-xs text-zinc-300 mt-0.5 line-clamp-2">{h.desc}</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/menu" className="btn-gold inline-flex px-8 py-4 rounded-xl items-center gap-2" data-testid="lezzet-order-btn">
              Tüm Menü & Sipariş <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* WHY DIRECT — marketing */}
      <section id="hakkimizda" className="relative py-20 px-5 sm:px-8 bg-[#101013]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-4">
            <span className="font-mono text-xs uppercase tracking-[0.3em] text-[#E5BF47]">Doğrudan CAFE-İN'den</span>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl font-semibold text-white">Neden Aracısız, Doğrudan Bizden?</h2>
          </div>
          <p className="text-center text-zinc-300 max-w-3xl mx-auto leading-relaxed text-sm sm:text-base">
            Popüler sipariş uygulamaları restoranlardan yüksek komisyonlar alır — bu maliyet eninde sonunda fiyata yansır.
            Biz siparişinizi <span className="text-[#FFE599]">doğrudan</span> alıyoruz: aradaki komisyon cebimizde kalıyor,
            karşılığında size <span className="text-[#FFE599]">daha uygun fiyat, daha taze ürün ve daha hızlı servis</span> olarak geri dönüyor.
          </p>

          <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5">
            {[
              { icon: PiggyBank, t: "Komisyon Yok", d: "Aracıya komisyon ödemiyoruz; bu avantaj doğrudan sizin cebinize kalır." },
              { icon: Banknote, t: "Kapıda Ödeme", d: "Nakit veya kart (POS) ile kapıda ödeyin. Online kart zorunluluğu yok." },
              { icon: Zap, t: "Taze & Hızlı", d: "Sipariş doğrudan bize düşer; aracı beklemesi olmadan taze ve hızlı hazırlanır." },
              { icon: ShieldCheck, t: "Güvenli Üyelik", d: "Üyelik ve telefon onayı ile yalnızca gerçek siparişler — sahte sipariş yok." },
            ].map((c, i) => {
              const Icon = c.icon;
              return (
                <div key={c.t} className="card-lux card-hover p-5 sm:p-6 fade-up" style={{ animationDelay: `${i * 0.07}s` }} data-testid={`why-${i}`}>
                  <div className="h-11 w-11 rounded-xl bg-[#D4AF37]/12 border border-[#D4AF37]/25 flex items-center justify-center"><Icon className="text-[#D4AF37]" size={22} /></div>
                  <h3 className="font-serif text-lg sm:text-xl text-white mt-4">{c.t}</h3>
                  <p className="text-xs sm:text-sm text-zinc-400 mt-2 leading-relaxed">{c.d}</p>
                </div>
              );
            })}
          </div>

          <div className="mt-8 text-center">
            <div className="inline-flex flex-wrap items-center justify-center gap-2 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/30 px-5 py-2.5">
              <Banknote size={16} className="text-[#D4AF37]" />
              <span className="text-sm text-[#FFE599]">Ödeme kapıda: Nakit veya Kart (POS)</span>
            </div>
            <div className="mt-6">
              <Link to="/menu" className="btn-gold inline-flex px-8 py-4 rounded-xl items-center gap-2" data-testid="why-order-btn">Doğrudan Sipariş Ver <ArrowRight size={18} /></Link>
            </div>
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="iletisim" className="relative py-20 px-5 sm:px-8">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-8 items-stretch">
          <div className="card-lux p-8 flex flex-col justify-center gap-6">
            <div>
              <span className="font-mono text-xs uppercase tracking-[0.3em] text-[#E5BF47]">İletişim & Konum</span>
              <h2 className="mt-3 font-serif text-3xl font-semibold text-white">Bize Uğrayın</h2>
            </div>
            <div className="space-y-5">
              <div className="flex items-start gap-4"><MapPin className="text-[#D4AF37] shrink-0 mt-0.5" size={20} /><p className="text-zinc-300 text-sm">{s.address || "Demirtaş Dumlupınar Mah. Ayazma Cad. No:16/1 Osmangazi/Bursa"}</p></div>
              <div className="flex items-center gap-4"><Phone className="text-[#D4AF37] shrink-0" size={20} /><a href={`tel:${s.phone}`} className="text-zinc-300 text-sm hover:text-[#FFE599]">{s.phone || "0224 000 00 00"}</a></div>
              <div className="flex items-center gap-4"><Clock className="text-[#D4AF37] shrink-0" size={20} /><p className="text-zinc-300 text-sm">{s.hours || "Her gün 09:00 - 23:00"}</p></div>
              {s.instagram && <div className="flex items-center gap-4"><Instagram className="text-[#D4AF37] shrink-0" size={20} /><a href={s.instagram} className="text-zinc-300 text-sm hover:text-[#FFE599]">Instagram</a></div>}
            </div>
            <Link to="/menu" className="btn-gold px-6 py-3.5 rounded-xl text-center" data-testid="contact-order-btn">Sipariş Ver</Link>
          </div>
          <div className="card-lux overflow-hidden min-h-[340px]">
            <iframe title="CAFE-İN Konum" src={s.mapUrl || "https://www.google.com/maps?q=Osmangazi+Bursa&output=embed"} className="w-full h-full min-h-[340px]" style={{ border: 0, filter: "grayscale(0.3) invert(0.9) contrast(0.9)" }} loading="lazy" />
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-[#D4AF37]/15 py-10 px-5 sm:px-8">
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src="/cafein-icon.png" alt="CAFE-İN" className="h-9 w-9 object-contain" />
            <span className="font-serif text-lg gold-text">CAFE-İN</span>
          </div>
          <p className="text-xs text-zinc-500 font-mono text-center">© {new Date().getFullYear()} CAFE-İN — Osmangazi / Bursa</p>
        </div>
      </footer>

      <ChatWidget />
    </div>
  );
}
